import { getOperator, resolveDefaultFilterOperator, resolveFilterColumnType } from './filter-meta'
import { isActiveFilter, isEmptyFilterValue, isValuelessOperator } from './filter-utils'
import type { GridColumnInfo, GridFilter, GridFilterGroupState, GridFilterOperator } from '../types'

let filterIdSeq = 0

function toFilterGroups(
  groups: GridFilterGroupState[] | null | undefined
): GridFilterGroupState[] {
  return Array.isArray(groups) ? groups : []
}

export function createFilterId(prefix = 'f') {
  filterIdSeq += 1
  return `${prefix}_${filterIdSeq}`
}

export function createFilterCondition(column: GridColumnInfo): GridFilter {
  const type = resolveFilterColumnType(column.type ?? 'base')
  return {
    id: createFilterId('cond'),
    field: column.field!,
    operator: resolveDefaultFilterOperator(type, column),
    type,
    value: null,
    operatorList: getOperator(type),
    logic: 'and',
  }
}

export function createFilterGroup(column?: GridColumnInfo): GridFilterGroupState {
  const group: GridFilterGroupState = {
    id: createFilterId('group'),
    logic: 'and',
    groupLogic: 'or',
    conditions: [],
  }
  if (column?.field) {
    group.conditions.push(createFilterCondition(column))
  }
  return group
}

export function ensureDefaultFilterGroups(
  groups: GridFilterGroupState[] | null | undefined,
  column?: GridColumnInfo
) {
  const normalized = toFilterGroups(groups)
  if (normalized.length > 0) {
    return normalized
  }
  if (!column?.field) {
    return normalized
  }
  normalized.push(createFilterGroup(column))
  return normalized
}

export function flattenFilterConditions(
  groups: GridFilterGroupState[] | null | undefined
): GridFilter[] {
  return toFilterGroups(groups).flatMap((group) => group.conditions)
}

export function hasActiveFilterGroups(
  groups: GridFilterGroupState[] | null | undefined
) {
  return flattenFilterConditions(groups).some(isActiveFilter)
}

export function removeFieldFromFilterGroups(
  groups: GridFilterGroupState[] | null | undefined,
  field: string
) {
  const normalized = toFilterGroups(groups)
  for (const group of normalized) {
    group.conditions = group.conditions.filter((cond) => cond.field !== field)
  }
  return normalized.filter((group) => group.conditions.length > 0)
}

export function findFirstFieldCondition(
  groups: GridFilterGroupState[] | null | undefined,
  field: string
): GridFilter | undefined {
  for (const group of toFilterGroups(groups)) {
    const found = group.conditions.find((cond) => cond.field === field)
    if (found) {
      return found
    }
  }
  return undefined
}

export function upsertFieldConditionInFirstGroup(
  groups: GridFilterGroupState[] | null | undefined,
  column: GridColumnInfo,
  condition: GridFilter
) {
  if (!Array.isArray(groups)) {
    return
  }
  removeFieldFromFilterGroups(groups, column.field!)
  if (!groups.length) {
    groups.push(createFilterGroup())
  }
  groups[0].conditions.push({
    ...condition,
    id: createFilterId('cond'),
    logic: groups[0].conditions.length > 0 ? 'and' : 'and',
  })
}

export function isActiveColumnMenuFilter(operator: GridFilterOperator, value: unknown) {
  if (isValuelessOperator(operator)) {
    return true
  }
  return !isEmptyFilterValue(value)
}

/** 移除无效条件与空组，apply 筛选前调用 */
export function sanitizeFilterGroups(
  groups: GridFilterGroupState[] | null | undefined
): GridFilterGroupState[] {
  return toFilterGroups(groups)
    .map((group) => ({
      ...group,
      conditions: group.conditions.filter(isActiveFilter),
    }))
    .filter((group) => group.conditions.length > 0)
}
